import React, { useEffect, useRef } from 'react';
import { useReducedMotion } from '../animations/useReducedMotion';
import './bayer-dither.css';

/**
 * Bayer 8x8 ordered-dither WebGL1 wave shader adapted from thesysdev/openui
 * (`packages/react-ui/src/components/AgentInterface/WelcomeDither.tsx`, MIT).
 *
 * Reads its foreground ink from the element's CSS `color` property so it
 * automatically follows light and dark themes. Gated by IntersectionObserver,
 * `document.hidden`, and `prefers-reduced-motion`, with a static CSS stipple
 * fallback when WebGL is unavailable or reduced motion is preferred.
 */

const VERTEX_SHADER = `
attribute vec2 a_position;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER = `
precision mediump float;

uniform vec2 u_resolution;
uniform float u_pixel;
uniform float u_time;
uniform vec4 u_color;

const float TAU = 6.28318530718;

float bayerQuad(float a, float b) {
  return b * 3.0 + a * 2.0 - a * b * 4.0;
}

float bayer8(vec2 coord) {
  vec2 t = mod(coord, 8.0);
  vec2 c2 = mod(t, 2.0);
  vec2 c4 = floor(mod(t, 4.0) / 2.0);
  vec2 c8 = floor(t / 4.0);
  return (bayerQuad(c2.x, c2.y) * 16.0 + bayerQuad(c4.x, c4.y) * 4.0 + bayerQuad(c8.x, c8.y)) / 64.0;
}

float sineField(vec2 uv) {
  float aspect = u_resolution.x / max(u_resolution.y, 1.0);
  vec2 p = vec2((uv.x - 0.58) * aspect, uv.y - 0.48);
  float a = 0.418879;
  vec2 r = vec2(p.x * cos(a) - p.y * sin(a), p.x * sin(a) + p.y * cos(a));
  float wave = sin(r.x * 0.55 * TAU + u_time) * 0.16 + cos(r.y * 0.4 * TAU - u_time * 0.6) * 0.08;
  float dist = abs(r.y - wave);
  return 1.0 - smoothstep(0.0, 0.58, dist);
}

void main() {
  vec2 coord = floor(gl_FragCoord.xy / u_pixel);
  vec2 uv = (coord + 0.5) * u_pixel / u_resolution;
  float edgeFade = smoothstep(0.0, 0.28, uv.x) * (1.0 - smoothstep(0.75, 1.0, uv.x)) *
                   smoothstep(0.0, 0.22, uv.y) * (1.0 - smoothstep(0.78, 1.0, uv.y));
  float lum = edgeFade * sineField(uv);
  float threshold = bayer8(coord);
  float on = step(threshold, lum * 0.82);
  gl_FragColor = vec4(u_color.rgb * u_color.a * on, u_color.a * on);
}
`;

function compileShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        gl.deleteShader(shader);
        return null;
    }
    return shader;
}

function parseCssColor(el: Element): [number, number, number, number] {
    const raw = getComputedStyle(el).color;
    const nums = (raw.match(/[\d.]+/g) ?? []).map(Number);
    if (raw.startsWith('color(')) {
        const [r = 0.5, g = 0.7, b = 0.3, a = 1] = nums;
        return [r, g, b, a];
    }
    const [r = 128, g = 180, b = 90, a = 1] = nums;
    return [r / 255, g / 255, b / 255, a];
}

export const BayerDither: React.FC<{
    className?: string;
    pixelSize?: number;
}> = ({ className = '', pixelSize = 4 }) => {
    const reduced = useReducedMotion();
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        if (reduced) return;
        const canvas = canvasRef.current;
        if (!canvas || typeof IntersectionObserver === 'undefined' || /jsdom/i.test(navigator.userAgent)) return;

        let gl: WebGLRenderingContext | null = null;
        try {
            gl = canvas.getContext('webgl', {
                alpha: true,
                antialias: false,
                depth: false,
                stencil: false,
                premultipliedAlpha: true,
            });
        } catch {
            return;
        }
        if (!gl) return;

        const vs = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
        const fs = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
        if (!vs || !fs) return;

        const program = gl.createProgram();
        if (!program) return;
        gl.attachShader(program, vs);
        gl.attachShader(program, fs);
        gl.linkProgram(program);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;

        const buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(
            gl.ARRAY_BUFFER,
            new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
            gl.STATIC_DRAW,
        );

        const posLoc = gl.getAttribLocation(program, 'a_position');
        const resLoc = gl.getUniformLocation(program, 'u_resolution');
        const pixLoc = gl.getUniformLocation(program, 'u_pixel');
        const timeLoc = gl.getUniformLocation(program, 'u_time');
        const colLoc = gl.getUniformLocation(program, 'u_color');

        let inView = false;
        let raf = 0;
        const start = performance.now();

        const render = (now: number) => {
            if (!inView || document.hidden || !gl) {
                raf = 0;
                return;
            }
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const w = Math.max(1, Math.round(canvas.clientWidth * dpr));
            const h = Math.max(1, Math.round(canvas.clientHeight * dpr));
            if (canvas.width !== w || canvas.height !== h) {
                canvas.width = w;
                canvas.height = h;
                gl.viewport(0, 0, w, h);
            }
            const [r, g, b, a] = parseCssColor(canvas);
            gl.useProgram(program);
            gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
            gl.enableVertexAttribArray(posLoc);
            gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);
            gl.uniform2f(resLoc, w, h);
            gl.uniform1f(pixLoc, pixelSize * dpr);
            gl.uniform1f(timeLoc, ((now - start) / 1000) * 0.9);
            gl.uniform4f(colLoc, r, g, b, a * 0.26);
            gl.drawArrays(gl.TRIANGLES, 0, 6);
            raf = requestAnimationFrame(render);
        };

        const sync = () => {
            if (inView && !document.hidden && !raf) {
                raf = requestAnimationFrame(render);
            }
        };

        const observer = new IntersectionObserver(
            entries => {
                inView = entries.some(e => e.isIntersecting);
                sync();
            },
            { rootMargin: '60px' },
        );
        observer.observe(canvas);
        document.addEventListener('visibilitychange', sync);

        return () => {
            if (raf) cancelAnimationFrame(raf);
            observer.disconnect();
            document.removeEventListener('visibilitychange', sync);
        };
    }, [reduced, pixelSize]);

    return (
        <div data-testid="bayer-dither" className={`bs-bayer-dither ${className}`.trim()} aria-hidden="true">
            <canvas ref={canvasRef} className="bs-bayer-dither__canvas" />
        </div>
    );
};

export default BayerDither;
